import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { supabase } from '@/lib/supabase';
import { getPortMeta, PORTS } from '@/data/ports';
import type { Character } from '@/types/database';

interface DashboardHeaderProps {
  character: Character;
}

const PORT_IDS = [
  'port_royale',
  'tortuga',
  'nassau',
  'charleston',
  'havana',
  'saint_domingue',
  'maracaibo',
];

export function DashboardHeader({ character }: DashboardHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const refreshCharacter = useAuthStore((s) => s.refreshCharacter);
  const [signingOut, setSigningOut] = useState(false);
  const [portOpen, setPortOpen] = useState(false);
  const [changingPort, setChangingPort] = useState(false);
  const port = getPortMeta(character.current_port);

  const availablePorts = !character.tutorial_complete ? ['cutlass_cove', ...PORT_IDS] : PORT_IDS;

  async function handlePortChange(destination: string): Promise<void> {
    setChangingPort(true);
    setPortOpen(false);
    try {
      const { data } = await supabase.functions.invoke('change-port', {
        body: { characterId: character.id, destination },
      });
      if (data?.ok) {
        await refreshCharacter();
      }
    } catch {
      // Silent
    } finally {
      setChangingPort(false);
    }
  }

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/', { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <header className="relative w-full overflow-hidden">
      {/* Port banner image */}
      <img
        src={port.banner}
        alt={`${port.name} port banner`}
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />

      {/* Gradient overlays for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/30" />

      {/* Sign Out button — floating top right */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-5 z-10">
        <button
          type="button"
          onClick={() => {
            void handleSignOut();
          }}
          disabled={signingOut}
          className="inline-flex items-center justify-center rounded-md border border-parchment/30 bg-black/40 backdrop-blur-sm px-3 py-2 text-[11px] sm:text-xs font-display uppercase tracking-wider text-parchment-dark hover:border-blood-bright hover:text-blood-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-candle transition-colors min-h-11 disabled:opacity-60"
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>

      {/* Character info — bottom left, over the gradient */}
      <div className="relative z-[5] flex items-end justify-between px-4 sm:px-6 pt-24 sm:pt-36 lg:pt-48 pb-4 sm:pb-5">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl text-parchment drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] truncate">
            {character.name}
          </h1>
          <div className="text-xs sm:text-sm text-parchment-dark/90 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            Level <span className="font-mono text-parchment">{character.level}</span>
            <span className="mx-1.5 text-candle/60">&middot;</span>
            <span className="italic">{character.title}</span>
          </div>
        </div>

        {/* Port selector — right side */}
        <div className="relative">
          <button
            type="button"
            onClick={() => character.state === 'free' && setPortOpen(!portOpen)}
            disabled={character.state !== 'free' || changingPort}
            className="flex items-center gap-2 text-parchment drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] hover:text-candle transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span aria-hidden className="text-candle text-lg">
              &#x2693;
            </span>
            <div className="text-right">
              <div className="font-display text-base lg:text-lg tracking-[0.2em] uppercase">
                {changingPort ? 'Sailing...' : port.name}
              </div>
              <div className="text-[11px] text-parchment-dark/80 italic">{port.tagline}</div>
            </div>
            <span className="text-parchment-dark/60 text-xs">&#x25BC;</span>
          </button>

          {portOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border-2 border-wood-mid/70 bg-sea-deep/95 backdrop-blur shadow-xl z-20 overflow-hidden">
              {availablePorts.map((pid) => {
                const p = PORTS[pid] ?? { name: pid };
                const isCurrent = pid === character.current_port.replace(/-/g, '_');
                return (
                  <button
                    key={pid}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => {
                      void handlePortChange(pid);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      isCurrent
                        ? 'text-candle bg-sea-dark/50 cursor-default'
                        : 'text-parchment-dark hover:text-parchment hover:bg-sea-dark/50'
                    }`}
                  >
                    <span className="font-display text-xs uppercase tracking-wider">{p.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Port name on small screens */}
      <div className="relative z-[5] sm:hidden flex items-center justify-center gap-1.5 pb-3 text-parchment-dark text-xs drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
        <button
          type="button"
          onClick={() => character.state === 'free' && setPortOpen(!portOpen)}
          disabled={character.state !== 'free'}
          className="flex items-center gap-1.5 hover:text-candle transition-colors disabled:opacity-60"
        >
          <span aria-hidden className="text-candle">
            &#x2693;
          </span>
          <span className="font-display tracking-[0.2em] uppercase">
            {changingPort ? 'Sailing...' : port.name}
          </span>
          <span className="text-parchment-dark/60">&#x25BC;</span>
        </button>
      </div>
    </header>
  );
}
